import { z } from 'zod';

// ── Web Push subscriptions (§36 push channel, Phase 2C) ────────────────────────
/** base64url, with or without padding (browsers differ); normalized without it. */
const base64url = (bytes: number, label: string) => {
  const chars = Math.ceil((bytes * 4) / 3);
  return z
    .string()
    .regex(new RegExp(`^[A-Za-z0-9_-]{${chars}}={0,2}$`), `${label} must be ${bytes} bytes, base64url`)
    .transform((v) => v.replace(/=+$/, ''));
};

const endpoint = z.url('Must be a URL').max(2048);

/** The browser's `PushSubscription.toJSON()`. */
export const pushSubscriptionSchema = z
  .object({
    endpoint,
    expirationTime: z.number().nullable().optional(),
    keys: z
      .object({
        /** The browser's P-256 public key (uncompressed point). */
        p256dh: base64url(65, 'p256dh'),
        /** The 16-byte authentication secret. */
        auth: base64url(16, 'auth'),
      })
      .strict(),
  })
  .strict();
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

export const removePushSubscriptionSchema = z.object({ endpoint }).strict();
export type RemovePushSubscriptionInput = z.infer<typeof removePushSubscriptionSchema>;

// ── Native push of the Android app (Phase 3J, ADR-065) ─────────────────────────
/** A Firebase Cloud Messaging registration token, as the push plugin reports it. */
const fcmToken = z
  .string('Missing the registration token')
  .regex(/^[A-Za-z0-9_:-]{20,1024}$/, 'Not a Firebase Cloud Messaging registration token');

/** Registers the Android app on this device for the signed-in user's pushes. */
export const nativePushTokenSchema = z.object({ platform: z.literal('ANDROID'), token: fcmToken }).strict();
export type NativePushTokenInput = z.infer<typeof nativePushTokenSchema>;

export const removeNativePushTokenSchema = z.object({ token: fcmToken }).strict();
export type RemoveNativePushTokenInput = z.infer<typeof removeNativePushTokenSchema>;
